import {PATH_KEYS} from "@/services/appServices";
import {useAuth0} from "@auth0/auth0-react";
import {useItemsCrud} from "./useItemsCrud";
import {useItemService} from "./useServices";
import {
  FavoriteLegislationRequest,
  FavoriteWorkspaceDocumentRequest,
  RemoveFavoriteLegislationRequest,
  RemoveFavoriteWorkspaceDocumentRequest,
  RenameFavoriteLegislationRequest,
  RenameFavoriteWorkspaceDocumentRequest,
  User,
  UsersFavoriteDocuments
} from "@/models/users/User";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Pagination, PaginationQuery} from "@/models/base/Pagination";
import {useNavigate} from "react-router-dom";
import AppRoutes from "@/constants/routes/AppRoutes";
import useUserTokenStore from "@/stores/user-token.store";
import { currentReturnToPath } from "@/lib/auth/authRedirectNavigate";
import { isClientAuthFailure, restError } from "@/models/client/RestResponse";
import { logger } from "@/lib/logger";
import {Subscription, SubscriptionRequest,} from "@/models/subscriptions/Subscription";
import {LegislationSubscriptionCreateRequest} from "@/models/subscriptions/LegislationSubscription.ts";
import useSubscriptionOperationStore from "@/stores/subscription.store";
import {Notification} from "@/models/notification/Notifications";
import {NotificationPagination} from "@/models/notification/NotificationPagination";
import useUserStore from "@/stores/user.store";
import useFavoriteOperationStore from "@/stores/favorites.store";
import { FavoriteSearchesResponse } from "@/services/searchServices";

const useUsers = () => {
  const { operationQueue } = useFavoriteOperationStore();
  const { operationQueue: subscribeOperationQueue } =
    useSubscriptionOperationStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth0();
  /**** Services ****/
  const userService = useItemService((services) => services.user);

  const userCrud = useItemsCrud<User>(
    [PATH_KEYS.user],
    (services) => services.user
  );

  /**** Queries & Mutations ****/

  const useQueryMe = (isLoginPage: boolean = false) =>
    useQuery<User>({
      queryKey: [PATH_KEYS.user, "me"],
      queryFn: async () => {
        const fetchedPageContent = await userService.getMe();
        if (fetchedPageContent.success) {
          return fetchedPageContent.value;
        } else {
          const isAuthStatus =
            fetchedPageContent.status === 401 ||
            fetchedPageContent.status === 403;

          if (!isLoginPage && isAuthStatus) {
            if (isClientAuthFailure(fetchedPageContent)) {
              // Synthetic 401: the request never reached the API because we
              // could not produce a token — typically the token stampede when a
              // hidden tab regains focus. Re-login is owned by authRecovery and
              // AuthenticationGuard; navigating here threw the user's location
              // away and landed them on the dashboard (LAR-361).
              logger.warn("Skipping signin redirect for client auth failure", {
                operation: "useQueryMe",
                clientAuthFailure: fetchedPageContent.clientAuthFailure,
              });
            } else {
              logger.warn("Auth error detected, redirecting to signin", {
                operation: "useQueryMe",
                status: fetchedPageContent.status,
              });
              useUserTokenStore.setState({ token: "" });
              // `returnTo` so finalizeLogin restores the page instead of
              // falling back to the dashboard (LAR-361).
              navigate(AppRoutes.auth, {
                replace: true,
                state: { returnTo: currentReturnToPath() },
              });
            }
          }

          throw restError(fetchedPageContent);
        }
      },
      enabled: isAuthenticated,
    });

  const useQueryAllTenantUsers = () =>
    useQuery<User[]>({
      queryKey: [PATH_KEYS.user],
      queryFn: async () => {
        const fetchedPageContent = await userService.getAllTenantUser();
        if (fetchedPageContent.success) {
          return fetchedPageContent.value;
        } else throw restError(fetchedPageContent);
      },
    });

  const onLogin = () => {
    if (useUserStore.getState().hasCalledLogin) return;
    useUserStore.getState().setHasCalledLogin(true);
    userService.onLogin();
  };

  const useQueryUsersFavoriteDocuments = () =>
    useQuery<UsersFavoriteDocuments>({
      queryKey: [PATH_KEYS.user, "favorite-documents"],
      queryFn: async () => {
        const fetchedPageContent =
          await userService.getUsersFavoriteDocuments();
        if (fetchedPageContent.success) {
          return fetchedPageContent.value;
        } else throw restError(fetchedPageContent);
      },
    });

  const addFavoriteLegislation = useMutation({
    mutationKey: [PATH_KEYS.user, "add-legislation"],
    mutationFn: async (request: FavoriteLegislationRequest) => {
      const documentId = request.externalId;
      useFavoriteOperationStore.setState((state) => ({
        pendingOperations: new Set(state.pendingOperations).add(documentId),
      }));

      try {
        const operation = async () => {
          const response = await userService.addFavoriteLegislation(request);

          if (response.success) {
            return response.value;
          }
          throw restError(response);
        };

        return await operationQueue.enqueue(operation);
      } finally {
        useFavoriteOperationStore.setState((state) => {
          const newPending = new Set(state.pendingOperations);
          newPending.delete(documentId);
          return { pendingOperations: newPending };
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "favorite-documents"],
      });
    },
  });

  const addFavoriteWorkspaceDocument = useMutation({
    mutationKey: [PATH_KEYS.user, "add-workspace-document"],
    mutationFn: async (request: FavoriteWorkspaceDocumentRequest) => {
      const documentId = request.documentId;
      useFavoriteOperationStore.setState((state) => ({
        pendingOperations: new Set(state.pendingOperations).add(documentId),
      }));

      try {
        const operation = async () => {
          const response =
            await userService.addFavoriteWorkspaceDocument(request);

          if (response.success) {
            return response.value;
          }
          throw restError(response);
        };

        return await operationQueue.enqueue(operation);
      } finally {
        useFavoriteOperationStore.setState((state) => {
          const newPending = new Set(state.pendingOperations);
          newPending.delete(documentId);
          return { pendingOperations: newPending };
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "favorite-documents"],
      });
    },
  });

  const removeFavoriteLegislation = useMutation({
    mutationKey: [PATH_KEYS.user, "remove-legislation"],
    mutationFn: async (request: RemoveFavoriteLegislationRequest) => {
      const documentId = request.externalId;
      useFavoriteOperationStore.setState((state) => ({
        pendingOperations: new Set(state.pendingOperations).add(documentId),
      }));

      try {
        const operation = async () => {
          const response = await userService.removeFavoriteLegislation(request);

          if (response.success) {
            return response.value;
          }
          throw restError(response);
        };

        return await operationQueue.enqueue(operation);
      } finally {
        useFavoriteOperationStore.setState((state) => {
          const newPending = new Set(state.pendingOperations);
          newPending.delete(documentId);
          return { pendingOperations: newPending };
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "favorite-documents"],
      });
    },
  });

  const removeFavoriteWorkspaceDocument = useMutation({
    mutationKey: [PATH_KEYS.user, "remove-workspace-document"],
    mutationFn: async (request: RemoveFavoriteWorkspaceDocumentRequest) => {
      const documentId = request.documentId;
      useFavoriteOperationStore.setState((state) => ({
        pendingOperations: new Set(state.pendingOperations).add(documentId),
      }));

      try {
        const operation = async () => {
          const response =
            await userService.removeFavoriteWorkspaceDocument(request);

          if (response.success) {
            return response.value;
          }
          throw restError(response);
        };

        return await operationQueue.enqueue(operation);
      } finally {
        useFavoriteOperationStore.setState((state) => {
          const newPending = new Set(state.pendingOperations);
          newPending.delete(documentId);
          return { pendingOperations: newPending };
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "favorite-documents"],
      });
    },
  });

  /**
   * Relabels a favorited legislation source (LAR-418). Goes through the same queue as
   * the add/remove mutations: every favorite write appends to the one User stream, so
   * two in flight at once would race for its version.
   */
  const renameFavoriteLegislation = useMutation({
    mutationKey: [PATH_KEYS.user, "rename-legislation"],
    mutationFn: async (request: RenameFavoriteLegislationRequest) => {
      const documentId = request.externalId;
      useFavoriteOperationStore.setState((state) => ({
        pendingOperations: new Set(state.pendingOperations).add(documentId),
      }));

      try {
        const operation = async () => {
          const response = await userService.renameFavoriteLegislation(request);

          if (response.success) {
            return response.value;
          }
          throw restError(response);
        };

        return await operationQueue.enqueue(operation);
      } finally {
        useFavoriteOperationStore.setState((state) => {
          const newPending = new Set(state.pendingOperations);
          newPending.delete(documentId);
          return { pendingOperations: newPending };
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "favorite-documents"],
      });
    },
  });

  /** Relabels a favorited workspace document (LAR-418). The document keeps its name. */
  const renameFavoriteWorkspaceDocument = useMutation({
    mutationKey: [PATH_KEYS.user, "rename-workspace-document"],
    mutationFn: async (request: RenameFavoriteWorkspaceDocumentRequest) => {
      const documentId = request.documentId;
      useFavoriteOperationStore.setState((state) => ({
        pendingOperations: new Set(state.pendingOperations).add(documentId),
      }));

      try {
        const operation = async () => {
          const response =
            await userService.renameFavoriteWorkspaceDocument(request);

          if (response.success) {
            return response.value;
          }
          throw restError(response);
        };

        return await operationQueue.enqueue(operation);
      } finally {
        useFavoriteOperationStore.setState((state) => {
          const newPending = new Set(state.pendingOperations);
          newPending.delete(documentId);
          return { pendingOperations: newPending };
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "favorite-documents"],
      });
    },
  });

  const reorderFavoriteDocuments = useMutation({
    mutationKey: [PATH_KEYS.user, "reorder-favorite-documents"],
    mutationFn: async (orderedIds: string[]) => {
      const response = await userService.reorderFavoriteDocuments(orderedIds);
      if (response.success) {
        return response.value;
      }
      throw restError(response);
    },
    onSuccess: (orderedIds) => {
      queryClient.setQueryData<UsersFavoriteDocuments>(
        [PATH_KEYS.user, "favorite-documents"],
        (current) =>
          current ? { ...current, displayOrder: orderedIds } : current
      );
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "favorite-documents"],
      });
    },
  });

  const reorderFavoriteSearches = useMutation({
    mutationKey: [PATH_KEYS.user, "reorder-favorite-searches"],
    mutationFn: async (orderedIds: string[]) => {
      const response = await userService.reorderFavoriteSearches(orderedIds);
      if (response.success) {
        return response.value;
      }
      throw restError(response);
    },
    onSuccess: (orderedIds) => {
      queryClient.setQueryData<FavoriteSearchesResponse>(
        [PATH_KEYS.search, "favorite-searches"],
        (current) =>
          current ? { ...current, displayOrder: orderedIds } : current
      );
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.search, "favorite-searches"],
      });
    },
  });

  const reorderSubscriptions = useMutation({
    mutationKey: [PATH_KEYS.user, "reorder-subscriptions"],
    mutationFn: async (orderedIds: string[]) => {
      const response = await userService.reorderSubscriptions(orderedIds);
      if (response.success) {
        return response.value;
      }
      throw restError(response);
    },
    onSuccess: (orderedIds) => {
      queryClient.setQueriesData<Pagination<Subscription>>(
        { queryKey: [PATH_KEYS.user, "subscriptions", "detailed"] },
        (current) =>
          current ? { ...current, displayOrder: orderedIds } : current
      );
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "subscriptions", "detailed"],
      });
    },
  });

  const useQuerySubscriptions = () =>
    useQuery<Subscription[]>({
      queryKey: [PATH_KEYS.user, "subscriptions"],
      queryFn: async () => {
        //This fetches all the subscriptions for the current user - unpaginated
        const fetchedPageContent = await userService.getSubscriptions();
        if (fetchedPageContent.success) {
          return fetchedPageContent.value;
        } else throw restError(fetchedPageContent);
      },
    });

  const useQueryDetailedSubscriptions = (
    pageNumber: number,
    pageSize = 20
  ) =>
    useQuery<Pagination<Subscription>>({
      queryKey: [PATH_KEYS.user, "subscriptions", "detailed", pageNumber, pageSize],
      queryFn: async () => {
        const fetchedPageContent = await userService.getDetailedSubscriptions(
          pageNumber,
          pageSize
        );
        if (fetchedPageContent.success) {
          return fetchedPageContent.value;
        } else throw restError(fetchedPageContent);
      },
      placeholderData: keepPreviousData,
    });

  const subscribe = useMutation({
    mutationKey: [PATH_KEYS.user, "subscribe"],
    mutationFn: async (
      subscribe: SubscriptionRequest
    ): Promise<Subscription> => {
      const { subscribeType, source, entityPath, entityName } = subscribe;

      // Add to pending operations
      useSubscriptionOperationStore.setState((state) => ({
        pendingOperations: new Set(state.pendingOperations).add(entityPath),
      }));

      try {
        const operation = async (): Promise<Subscription> => {
          const response = await userService.subscribe({
            subscribeType,
            source,
            entityPath,
            entityName,
          });

          if (response.success) {
            return response.value;
          }
          throw restError(response);
        };

        const result = await subscribeOperationQueue.enqueue(operation);
        return result as Subscription;
      } finally {
        // Remove from pending operations
        useSubscriptionOperationStore.setState((state) => {
          const newPending = new Set(state.pendingOperations);
          newPending.delete(entityPath);
          return { pendingOperations: newPending };
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "subscriptions"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "subscriptions", "detailed"],
      });
    },
  });
  const unsubscribe = useMutation({
    mutationKey: [PATH_KEYS.user, "unsubscribe"],
    mutationFn: async (subscribe: SubscriptionRequest) => {
      const { subscribeType, source, entityPath, entityName } = subscribe;

      // Add to pending operations
      useSubscriptionOperationStore.setState((state) => ({
        pendingOperations: new Set(state.pendingOperations).add(entityPath),
      }));

      try {
        const operation = async (): Promise<void> => {
          const response = await userService.unsubscribe({
            subscribeType,
            source,
            entityPath,
            entityName,
          });

          if (!response.success) {
            throw restError(response);
          }
        };

        await subscribeOperationQueue.enqueue(operation);
      } finally {
        // Remove from pending operations
        useSubscriptionOperationStore.setState((state) => {
          const newPending = new Set(state.pendingOperations);
          newPending.delete(entityPath);
          return { pendingOperations: newPending };
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "subscriptions"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "subscriptions", "detailed"],
      });
    },
  });

  const createLegislationSubscription = useMutation({
    mutationKey: [PATH_KEYS.user, "create-legislation-subscription"],
    mutationFn: async (
      request: LegislationSubscriptionCreateRequest
    ) => {
      const entityPath = `legislation/${request.externalId}/datasource/${request.datasource}`;
      useSubscriptionOperationStore.setState((state) => ({
        pendingOperations: new Set(state.pendingOperations).add(entityPath),
      }));
      try {
        const operation = async () => {
          const response = await userService.createLegislationSubscription(request);
          if (response.success) return response.value;
          throw restError(response);
        };
        return await subscribeOperationQueue.enqueue(operation);
      } finally {
        useSubscriptionOperationStore.setState((state) => {
          const next = new Set(state.pendingOperations);
          next.delete(entityPath);
          return { pendingOperations: next };
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "subscriptions"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "subscriptions", "detailed"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "legislation-timeline-check"],
      });
    },
  });

  const useLegislationTimelineCheck = (
    externalId: string | undefined,
    datasource: string | undefined,
    enabled: boolean
  ) => {
    return useQuery({
      queryKey: [
        PATH_KEYS.user,
        "legislation-timeline-check",
        externalId ?? "",
        datasource ?? "",
      ],
      queryFn: async () => {
        if (externalId == null || datasource == null)
          throw new Error("externalId and datasource required");
        const response =
          await userService.checkLegislationTimelineSubscription(
            externalId,
            datasource
          );
        if (response.success) return response.value;
        throw restError(response);
      },
      enabled:
        enabled &&
        (externalId?.trim() ?? "") !== "" &&
        (datasource?.trim() ?? "") !== "",
      staleTime: 30_000,
    });
  };

  const removeLegislationSubscription = useMutation({
    mutationKey: [PATH_KEYS.user, "remove-legislation-subscription"],
    mutationFn: async (subscriptionId: string) => {
      const pendingKey = `legislation-${subscriptionId}`;
      useSubscriptionOperationStore.setState((state) => ({
        pendingOperations: new Set(state.pendingOperations).add(pendingKey),
      }));
      try {
        const operation = async () => {
          const response = await userService.removeLegislationSubscription({
            subscriptionId,
          });
          if (!response.success) throw restError(response);
        };
        await subscribeOperationQueue.enqueue(operation);
      } finally {
        useSubscriptionOperationStore.setState((state) => {
          const next = new Set(state.pendingOperations);
          next.delete(pendingKey);
          return { pendingOperations: next };
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "subscriptions"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "subscriptions", "detailed"],
      });
    },
  });

  const useQueryAllNotifications = (query: PaginationQuery) =>
    useQuery<Pagination<Notification>>({
      queryKey: [
        PATH_KEYS.user,
        "notifications",
        query.pageNumber,
        query.pageSize,
      ],
      queryFn: async () => {
        const fetchedPageContent = await userService.getAllNotifications(query);
        if (fetchedPageContent.success) {
          return fetchedPageContent.value;
        } else throw restError(fetchedPageContent);
      },
    });

  const useQueryActiveAndSeenNotifications = (query: PaginationQuery) =>
    useQuery<NotificationPagination>({
      queryKey: [
        PATH_KEYS.user,
        "notifications",
        "active",
        query.pageNumber,
        query.pageSize,
      ],
      queryFn: async () => {
        const fetchedPageContent =
          await userService.getActiveAndSeenNotifications(query);
        if (fetchedPageContent.success) {
          return fetchedPageContent.value;
        } else throw restError(fetchedPageContent);
      },
    });

  const markAllNotificationsAsSeen = useMutation({
    mutationKey: [PATH_KEYS.user, "mark-all-notifications-as-seen"],
    mutationFn: async () => {
      const response = await userService.markAllNotificationsAsSeen();
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "notifications", "active"],
      });
    },
  });

  const markNotificationsAsSeen = useMutation({
    mutationKey: [PATH_KEYS.user, "mark-notifications-as-seen"],
    mutationFn: async (notificationIds: string[]) => {
      const response =
        await userService.markNotificationsAsSeen(notificationIds);
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "notifications", "active"],
      });
    },
  });

  const dismissNotification = useMutation({
    mutationKey: [PATH_KEYS.user, "dismiss-notification"],
    mutationFn: async (notificationId: string) => {
      const response = await userService.dismissNotification(notificationId);
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "notifications"],
      });
    },
  });

  const dismissNotifications = useMutation({
    mutationKey: [PATH_KEYS.user, "dismiss-notifications"],
    mutationFn: async (notificationIds: string[]) => {
      const response = await userService.dismissNotifications(notificationIds);
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "notifications"],
      });
    },
  });

  const dismissAllNotifications = useMutation({
    mutationKey: [PATH_KEYS.user, "dismiss-all-notifications"],
    mutationFn: async () => {
      const response = await userService.dismissAllNotifications();
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "notifications"],
      });
    },
  });

  const deleteNotifications = useMutation({
    mutationKey: [PATH_KEYS.user, "delete-notifications"],
    mutationFn: async (notificationIds: string[]) => {
      const response = await userService.deleteNotifications(notificationIds);
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "notifications"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "notifications", "active"],
      });
    },
  });

  const deleteAllNotifications = useMutation({
    mutationKey: [PATH_KEYS.user, "delete-all-notifications"],
    mutationFn: async () => {
      const response = await userService.deleteAllNotifications();
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "notifications"],
      });
      queryClient.invalidateQueries({
        queryKey: [PATH_KEYS.user, "notifications", "active"],
      });
    },
  });

  /**** Value ****/
  return {
    ...userCrud,
    useQueryMe,
    useQueryAllTenantUsers,
    onLogin,

    useQueryUsersFavoriteDocuments,
    addFavoriteLegislation,
    addFavoriteWorkspaceDocument,
    removeFavoriteLegislation,
    removeFavoriteWorkspaceDocument,
    renameFavoriteLegislation,
    renameFavoriteWorkspaceDocument,
    reorderFavoriteDocuments,
    reorderFavoriteSearches,
    reorderSubscriptions,
    favoritizingLoading: (documentId: string) =>
      addFavoriteLegislation.isPending ||
      addFavoriteWorkspaceDocument.isPending ||
      removeFavoriteLegislation.isPending ||
      removeFavoriteWorkspaceDocument.isPending ||
      useFavoriteOperationStore.getState().isPending(documentId),

    useQuerySubscriptions,
    useQueryDetailedSubscriptions,
    subscribe,
    unsubscribe,
    createLegislationSubscription,
    removeLegislationSubscription,
    useLegislationTimelineCheck,
    subscribeLoading: (pendingKey: string) =>
      subscribe.isPending ||
      unsubscribe.isPending ||
      createLegislationSubscription.isPending ||
      removeLegislationSubscription.isPending ||
      useSubscriptionOperationStore.getState().isPending(pendingKey),

    useQueryAllNotifications,
    useQueryActiveAndSeenNotifications,
    markAllNotificationsAsSeen,
    markNotificationsAsSeen,
    dismissNotification,
    dismissNotifications,
    dismissAllNotifications,
    deleteNotifications,
    deleteAllNotifications,
  };
};
export default useUsers;
